import { auto } from 'angular';
import { HttpService } from '../../../../App/services/http-service';
import { I18n } from '../../../../i18n/mx-internationalization';
import { OperationStatus } from '../../../Pandora/model/process-item';
import { DownloadHelpService } from '../../../Pandora/services/mx-download-help-service';
import { SchemaService } from '../../services/schema-service';
import { MxAttachmentProcessService } from './mx-attachment-process.service';
import { AttachmentMetaInfo, AttachmentObjectsRequest, IAttachmentProvider } from './mx-attachment.contracts';
import { MxAttachmentDefaultProvider } from './mx-providers/mx-attachment-default-provider';

/**
 * @ngdoc service
 * @name MxAttachmentService
 * @module mx.shell
 *
 * @description
 * Service provides methods to work with Attachments
 */

type AttachmentProviderStorage = {
	[providerId in string]: IAttachmentProvider;
};
export type StorageInfo = {
	id: string;
	providerId: string;
	isPublic: boolean;
	maxFileSize: number;
	typeId: string;
	fileExtensions: string;
	maxFilesPerObject: number;
};
type AttachmentFileInfo = {
	UniqueFileId: string;
	Name: string;
	Comment: string;
	Thumbnail: string;
	StorageId: string;
	ProviderId: string;
	ObjectId: string;
	BindAttribute: string;
	UpdatedBy: string;
	UpdatedOn?: Date;
	Uploaded?: boolean;
	Size: number;
	SendContentThroughServer: boolean;
};

export class MxAttachmentService {
	static $inject = [
		'mx.SolutionBuilderAgent.Http',
		'mx.internationalization',
		'mx.SolutionBuilderAgent.SchemaService',
		'mx.shell.DownloadHelpService',
		'mx.sb.Attachment.MxAttachmentProcessService',
		'$injector',
	];
	private providerStorage: AttachmentProviderStorage = {};
	constructor(
		private readonly http: HttpService,
		private readonly i18n: I18n,
		private readonly schemaService: SchemaService,
		private readonly downloadHelpService: DownloadHelpService,
		private readonly processService: MxAttachmentProcessService,
		$injector: auto.IInjectorService
	) {
		const defaultProviderInstance = $injector.instantiate<IAttachmentProvider>(MxAttachmentDefaultProvider);
		this.providerStorage[defaultProviderInstance.providerId] = defaultProviderInstance;
	}

	public registerProvider(providerId: string, provider: IAttachmentProvider): void {
		if (providerId in this.providerStorage) {
			throw new Error(this.i18n.get('solutionBuilderAgent.errors.attachment_provider_already_registered'));
		}
		this.providerStorage[providerId] = provider;
	}

	public async uploadFile(attachmentInfo: AttachmentMetaInfo): Promise<void> {
		const fileId = attachmentInfo.id;
		const providerId = attachmentInfo.providerId;
		const provider = this.getRegisteredProvider(providerId);
		const process = this.processService.createForegroundProcess(attachmentInfo);
		try {
			await provider.uploadFile(attachmentInfo, process);
			await this.http.post(`api/commonStorage/finishUploading/${fileId}`, {});
		} catch (e) {
			if (!process.isCanceled) {
				await process.fail();
				const msg = typeof e === 'string' ? e : e instanceof Error ? e.message : '';
				throw new Error(msg);
			}
		} finally {
			const status = process.operationStatus;
			if (status === OperationStatus.Started || status === OperationStatus.Updated) {
				process.operationStatus = OperationStatus.Finished;
			}
			if (status === OperationStatus.Canceling) {
				process.operationStatus === OperationStatus.Canceled;
			}
		}
	}

	public async downloadFiles(attachmentInfos: Array<AttachmentMetaInfo>): Promise<boolean> {
		if (attachmentInfos.length === 0) {
			return false;
		} else if (attachmentInfos.length === 1) {
			const attachmentInfo = attachmentInfos[0];
			const provider = this.getRegisteredProvider(attachmentInfo.providerId);
			return await provider.downloadFile(attachmentInfo);
		}
		const response = await this.http.post<ArrayBufferLike, true>(
			`api/commonStorage/`,
			attachmentInfos.map((info) => info.id),
			{ responseType: 'arraybuffer' },
			{},
			false,
			true
		);

		return this.downloadHelpService.downloadContent(response, response.data);
	}

	public async deleteFiles(attachmentInfos: Array<AttachmentMetaInfo>): Promise<void> {
		await Promise.all(attachmentInfos.map((info) => this.http.remove(`api/commonStorage/${info.id}`)));
	}

	public async getFiles(options: AttachmentObjectsRequest) {
		const objectIds = options.objectIds?.filter(Boolean) || [];
		if (objectIds.length === 0) {
			return [];
		}
		const paramBindAttribute = this.getBindAttributeParameter(options.bindAttribute);
		const paramExcludedFileIds = this.getExcludedFileIdsParameter(options.excludedFileIds);
		const objectFiles = await Promise.all(objectIds.map((objectId) => this.getFilesForObject(objectId, paramBindAttribute, paramExcludedFileIds)));
		return objectFiles.flat();
	}

	public async getCount(options: AttachmentObjectsRequest): Promise<number> {
		const objectIds = options.objectIds?.filter(Boolean) || [];
		if (objectIds.length === 0) {
			return 0;
		}
		const http = this.http;
		const paramBindAttribute = this.getBindAttributeParameter(options.bindAttribute);
		const paramExcludedFileIds = this.getExcludedFileIdsParameter(options.excludedFileIds);
		const objectsCount = await Promise.all(objectIds.map((objectId) => http.get<number>(`api/commonStorage/count/${objectId}${paramBindAttribute}${paramExcludedFileIds}`)));
		return objectsCount.reduce((sum, itemCount) => sum + itemCount, 0);
	}

	public async comment(fileId: string, comment: string): Promise<void> {
		await this.http.post(`api/commonStorage/comment/${fileId}`, `'${comment}'`);
	}

	public async getFileUrl(attachmentInfo: AttachmentMetaInfo): Promise<string> {
		const providerId = attachmentInfo.providerId;
		const provider = this.getRegisteredProvider(providerId);
		return await provider.getFileUrl(attachmentInfo);
	}

	public formatFileUrl(url: string): string {
		return /^((http|https|ftp):\/\/)/.test(url) && new URL(url).origin !== window.location.origin ? url : this.http.formatUrl(url);
	}

	private async getFilesForObject(objectId: string, bindAttribute = '', excludedFileIds = '') {
		const result = await this.http.get<Array<AttachmentFileInfo>>(`api/commonStorage/files/${objectId}${bindAttribute}${excludedFileIds}`);
		const files = await Promise.all(
			result.map(async (item) => {
				const uploadedOn = item.UpdatedOn ? new Date(item.UpdatedOn) : new Date();
				return {
					id: item.UniqueFileId,
					name: item.Name,
					comment: item.Comment,
					thumbnail: await this.getThumbnail(item, objectId, uploadedOn),
					uploadedBy: item.UpdatedBy,
					uploadedOn: uploadedOn,
					storageId: item.StorageId,
					providerId: item.ProviderId,
					uploaded: item.Uploaded,
					size: item.Size,
					objectId: objectId,
					isPublic: this.getRegisteredProvider(item.ProviderId).isPublic,
					sendContentThroughServer: item.SendContentThroughServer || false,
				};
			})
		);

		return files.filter((file) => file.uploaded || (!file.uploaded && Boolean(this.processService.getForegroundProcessById(file.id))));
	}

	public async getStorageInfo(entity: string): Promise<StorageInfo> {
		const mbModifier = 1024 * 1024;
		const typeInfo = await this.schemaService.getType(entity);
		if (!typeInfo) {
			throw new Error(`Type ${entity} doesn't exist`);
		}
		const response = await this.http.get<{ ProviderId: string; StorageId: string; MaxFileSizeMb: number; Extensions: string; FileCountPerObject: number }>(
			`api/commonStorage/provider/${typeInfo.Id}`
		);
		const provider = this.getRegisteredProvider(response.ProviderId);
		return {
			id: response.StorageId,
			providerId: provider.providerId,
			typeId: typeInfo.Id,
			maxFileSize: (response.MaxFileSizeMb || 0) * mbModifier,
			fileExtensions: response.Extensions,
			maxFilesPerObject: response.FileCountPerObject ?? 0,
			isPublic: provider.isPublic,
		};
	}

	private getRegisteredProvider(providerId: string): IAttachmentProvider {
		if (!(providerId in this.providerStorage)) {
			throw new Error(this.i18n.get('solutionBuilderAgent.errors.attachment_provider_not_registered'));
		}
		return this.providerStorage[providerId];
	}

	private getExcludedFileIdsParameter(excludedFileIds: Array<string> = []) {
		let parameterExcludedFileIds = '';
		if (excludedFileIds.length > 0) {
			parameterExcludedFileIds = '/?excludedFileIds=' + JSON.stringify(excludedFileIds).replace(/"/g, '').replace(/\[/g, '').replace(/]/g, '');
		}
		return parameterExcludedFileIds;
	}

	private getBindAttributeParameter(bindAttribute?: string) {
		let paramBindAttribute = '';
		const attribute = this.getBindAttribute(bindAttribute);
		if (attribute) {
			paramBindAttribute = '/' + attribute;
		}
		return paramBindAttribute;
	}

	private getBindAttribute(bindAttribute?: string) {
		let attribute = null;
		if (bindAttribute) {
			const lastIndexOfDot = bindAttribute.lastIndexOf('.');

			if (lastIndexOfDot > 0) {
				attribute = bindAttribute.substring(lastIndexOfDot + 1);
			} else {
				attribute = bindAttribute;
			}
		}
		return attribute;
	}

	private isVideo(fileName: string): boolean {
		const extension = (fileName.split('.').pop() || '').toUpperCase();
		return ['MP4', 'WEBM', 'OGG'].includes(extension);
	}

	private async getThumbnail(file: AttachmentFileInfo, objectId: string, uploadedOn: Date): Promise<string> {
		if (file.Thumbnail || !this.isVideo(file.Name) || !file.Uploaded) {
			return file.Thumbnail;
		}

		const attachmentInfo = {
			id: file.UniqueFileId,
			providerId: file.ProviderId,
			storageId: file.StorageId,
			uploadedOn: uploadedOn,
			uploadedBy: file.UpdatedBy,
			objectId: objectId,
			comment: file.Comment,
			uploaded: file.Uploaded,
			thumbnail: file.Thumbnail,
			sendContentThroughServer: file.SendContentThroughServer,
		};

		return this.formatFileUrl(await this.getFileUrl(attachmentInfo));
	}
}
